'use client';

import './styles/reset.css';
import './styles/siniat.css';
import './styles/custom-styling.css';
import '../../styles/abstract-common-bundle/_base-section-bundle.scss';
import './system-selector.component.scss';

import Button, {
  ButtonState,
  ButtonType,
} from '../../components/button/Button';
import { FilterType, IAccessoryFilter, IRowDataItem } from './interfaces';
import {
  IActions,
  IState,
  createAccessoryFilterFromFacetProps,
} from './actions';
import Icon, { IconType } from '../../components/icon/Icon';
import Link, { LinkVariant } from '../../components/link/Link';
import {
  TableHeadCellColumnSize,
  TableHeadCellProps,
  TableHeadCellSorting,
} from '../../components/table/TableHeadCell';
import {
  fetchSearchData,
  redirectToComparePage,
  redirectToSystemDetails,
} from '../services/dataService';
import { use, useEffect, useState } from 'react';

import AIModal from '../../components/modals/variants/ai/aiModal';
import Box from '../../components/box/Box';
import Card from '../../components/card/Card';
import { ExportButton } from './components/export/exportButton';
import Facets from '../../components/facets/Facets';
import { FilterComponent } from './components/filters';
import InfoPanel from '../../components/slideout/info-panel/infoPanel';
import InputField from '../../components/field/input/InputField';
import Pagination from '../../components/pagination/Pagination';
import { PictogramType } from '../../components/pictogram/Pictogram';
import QuickViewPanel from '../../components/slideout/quick-view-panel/quickView';
import SelectActionWidget from '../../components/select-action-widget/SelectActionWidget';
import SelectField from '../../components/field/select/SelectField';
import Table from '../../components/table/Table';
import { TagProps } from '../../components/tag/Tag';
import { TooltipPlacement } from '../../components/tooltip/tooltip';
import TopFilters from '../../components/top-filters/TopFilters';
import classNames from 'classnames';
import { hookIsMobile } from './hooks/hookIsMobile';
import { useReactHookz } from './state';

enum ViewType {
  TABLE = 'table',
  GRID = 'grid',
}

export const SystemSelector: React.FC = () => {
  const [state, actions]: [IState, IActions] = useReactHookz();
  const [isMobileView, setIsMobileView] = useState(false);
  const [viewType, setViewType] = useState(ViewType.TABLE);
  const [quickViewCard, setQuickViewCard] = useState<any>(null);
  const [quickViewVisible, setQuickViewVisible] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<
    Array<{ link: string; label: string; value: string }>
  >([]);
  const [colDefs, setColDefs] = useState<TableHeadCellProps[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const isMobile = hookIsMobile('--from-desktop');

  useEffect(() => {
    // Map state.contentHubData.listerFields to colDefs
    if (state.contentHubData?.listerFields) {
      const mappedColDefs = state.contentHubData.listerFields.map((f) => {
        return {
          field: f.field.name,
          label: f.field.label || f.field.name,
          isLeftSticky: f.isLeftSticky,
          ...(!f.field.disableSorting && {
            sort:
              state.contentHubData.sortFilter?.sortName === f.field.name
                ? state.contentHubData.sortFilter?.ascending
                  ? TableHeadCellSorting.ASCENDING
                  : TableHeadCellSorting.DESCENDING
                : (f.sort as TableHeadCellSorting),
          }),
          ...(f.field.name === 'name' && {
            columnSize: TableHeadCellColumnSize.HUGE,
          }),
          ...(f.field.name === 'description' && {
            columnSize: TableHeadCellColumnSize.XHUGE,
          }),
        };
      });

      setColDefs(mappedColDefs);
    }
  }, [state.contentHubData?.listerFields]);

  useEffect(() => {
    // Map state.contentHubData.list to rowData
    if (state.contentHubData?.list) {
      const mappedData = state.contentHubData.list.map((item: IRowDataItem) => {
        const row = item.properties.reduce((acc, property) => {
          acc[property.key] = property.value;
          return acc;
        }, {} as any);

        // Add favoriteCTA from the item itself if it exists
        row.favoriteCTA = item.favoriteCTA ?? false;

        // Add link for redirection
        row.link = item.link ?? '';

        return row;
      });

      setRowData(mappedData);
      window.scrollTo(0, 0)
    }
  }, [state.contentHubData?.list]);

  // Set initial view type based on screen size
  useEffect(() => {
    actions.getSiteConfiguration();
    actions.getSearchData(state.query);

    const checkScreenSize = () => {
      const isMobile = window.matchMedia('(max-width: 1024px)').matches;
      setIsMobileView(isMobile);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // force refresh of search when the token changes
  useEffect(() => {
    if (state.hasToken) {
      actions.getSearchData(state.query);
    }
  }, [state.hasToken]);

  const effectiveViewType = isMobileView ? ViewType.GRID : viewType;

  const handleBookmarkToggle = (currentTarget: any, id: string) => {
    // Logic to toggle bookmark state
    console.log(`Toggled bookmark for item with ID: ${id}`);
    actions.isFavorite(id)
      ? actions.deleteUserFavorite(id).then(() => {
          console.log(`Removed item with ID: ${id} from favorites`);
          // Remove the pulse effect after the action is complete
          currentTarget.classList.remove('a-link--pulse');
        })
      : actions.addToUserFavorite(id).then(() => {
          console.log(`Added item with ID: ${id} to favorites`);
          // Remove the pulse effect after the action is complete
          setTimeout(() => {
            currentTarget.classList.remove('a-link--pulse');
          }, 2500);
        });
  };

  const handleQuickView = (id: number) => {
    const card = state.contentHubData?.list?.find(
      (card) =>
        card.properties?.find((p) => p.key === 'id')?.value === id.toString()
    );

    if (card) {
      setQuickViewCard(card);
      setQuickViewVisible(true);
    }
  };

  const handleSelectAllToggle = (checked: boolean) => {
    if (checked) {
      rowData?.forEach((row) => {
        if (!state.selectedSystems.find((s) => s.id === row.id)) {
          actions.addToSelectedSystems(row);
        }
      });
    } else {
      rowData?.forEach((row) => {
        if (state.selectedSystems.find((s) => s.id === row.id)) {
          actions.removeSelectedSystem(row.id);
        }
      });
    }
  };

  const handleTableSort = (colDef: TableHeadCellProps) => {
    if (colDef.field) {
      actions.setSorting(
        colDef.field,
        colDef.sort != null ? setDestination(colDef.sort) : ''
      );
    }
  };

  const setDestination = (sort: TableHeadCellSorting) => {
    switch (sort) {
      case TableHeadCellSorting.DEFAULT:
        return TableHeadCellSorting.ASCENDING;
      case TableHeadCellSorting.ASCENDING:
        return TableHeadCellSorting.DESCENDING;
      default:
        return TableHeadCellSorting.DEFAULT;
    }
  };

  useEffect(() => {
    // This effect should only get search suggestions, not filter the table data
    if (searchQuery && searchQuery.length >= 3) {
      const getSearchSuggestions = async () => {
        setSearchLoading(true);

        try {
          const results = await fetchSearchData({
            ...state.query,
            query: searchQuery,
          });

          if (results && results.search && results.search.list) {
            setSearchLoading(false);
            console.log('Search suggestions:', results.search.list);
            setSearchResults(
              (results?.search?.list ?? [])
                .filter((item) => item && (item.id || item.name))
                .map((item) => {
                  const urlSlug =
                    item.id ||
                    item.name?.toLowerCase().replace(/\s+/g, '-') ||
                    '';
                  const link =
                    item.link ||
                    `/en-gb/products-and-systems/systems/partition-systems/${urlSlug}`;

                  return {
                    label: item.name || '',
                    value: item.id || '',
                    link,
                  };
                })
            );
          } else {
            setSearchLoading(false);
            setSearchResults([]);
          }
        } catch (error) {
          setSearchLoading(false);
          setSearchResults([]);
        }
      };
      getSearchSuggestions();
    }
  }, [searchQuery]);

  const handleSearch = () => {
    if (searchQuery.length > 2) {
      actions.setSearchQuery(searchQuery);
      actions.getSearchData({
        ...state.query,
        // selectedTextQueries is our BE output, we don't need to pass it in the selectFilters.
        // We have to attach the selectedTextQueries to the new searchQuery.
        query: `${state.contentHubData?.selectFilters?.selectedTextQueries.join(
          ' '
        )} ${searchQuery}`,
        // We need to pass the current selectFilters otherwise they are lost.
        // We are also mapping the filters because the state gives redundant fields.
        selectFilters: {
          filters: state.contentHubData?.selectFilters?.filters.map(
            (filter) => {
              return {
                value: filter.value,
                fieldName: filter.fieldName,
                filterType: filter.filterType,
                includedInTopFilter: filter.includedInTopFilter,
              };
            }
          ),
          selectedExclusiveTopFilter:
            state.contentHubData?.selectFilters?.selectedExclusiveTopFilter ||
            '',
          selectedNonExclusiveTopFilter: [
            ...(state.contentHubData?.selectFilters
              ?.selectedNonExclusiveTopFilter || []),
          ],
          selectedTextQueries: [
            ...(state.contentHubData?.selectFilters?.selectedTextQueries || []),
          ],
          favoritesTopFilterEnabled:
            state.contentHubData?.selectFilters?.favoritesTopFilterEnabled ||
            false,
        },
      });

      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleSearchClear = (): void => {
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="c-product-lister-isolated">
      <div className="c-product-lister">
        <div className="o-container o-container--max-width">
          <div className="c-product-lister__inner">
            <TopFilters
              primaryFilters={
                state.contentHubData?.topFilterListExclusive || []
              }
              secondaryFilters={[
                ...(state.contentHubData?.topFilterListNonExclusive &&
                state.contentHubData?.topFilterListNonExclusive.length > 0 &&
                state.token != null
                  ? [
                      {
                        id: 'favorites',
                        filterDisplayLabel: 'My Favorites',
                        active:
                          state.contentHubData?.selectFilters
                            ?.favoritesTopFilterEnabled,
                        prefilterItems: [],
                      },
                    ]
                  : []),
                ...(state.contentHubData?.topFilterListNonExclusive || []),
              ]}
              onPrimaryChange={(e) =>
                actions.setSelectedTopFilterExclusive(e.target.value)
              }
              onSecondaryChange={(e) => {
                if (e.target.value === 'favorites') {
                  actions.setFavoritesState(
                    !state.contentHubData?.selectFilters
                      ?.favoritesTopFilterEnabled
                  );
                  // return;
                }

                actions.setSelectedTopFilterNonExclusive(e.target.value);
              }}
            />

            <div className="c-product-lister__content">
              {!hookIsMobile('--from-desktop') && (
                <aside className="c-product-lister__aside ">
                  {!(
                    state.isLoading === false &&
                    state.contentHubData?.list.length === 0
                  ) && <FilterComponent />}
                </aside>
              )}

              <section className="c-product-lister__section">
                {/* TABLE ACTIONS (ex: search, export, view) */}
                <div className="c-product-lister__section-search u-mbe-6">
                  {hookIsMobile('--from-desktop') ? (
                    <>
                      <InputField
                        type="search"
                        id="search"
                        placeholder="Search for systems, properties or components"
                        value={searchQuery}
                        useAutocomplete={true}
                        autocompleteIsLoading={searchLoading}
                        submitLabel="Search"
                        searchResults={searchResults}
                        onSubmit={handleSearch}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={handleSearchClear}
                        onClickOptionSearch={(option) =>
                          redirectToSystemDetails(option)
                        }
                      />

                      <div className="filter-buttons-mobile">
                        <FilterComponent />

                        <SelectField
                          id="sorting"
                          options={[
                            {
                              label: 'Relevance',
                              name: 'Relevance',
                              value: 'Relevance',
                            },
                            ...(state.contentHubData
                              ?.attributesForSortingDropdown || []),
                          ]}
                          // value={state.contentHubData?.sortFilter?.sortName}
                          onChange={(value) => {
                            const [field, direction] = value.split('-');

                            try {
                              actions.setSorting(
                                field,
                                direction as TableHeadCellSorting
                              );
                            } catch (error) {
                              if (field === 'Relevance') {
                                actions.setSorting(
                                  '',
                                  TableHeadCellSorting.DEFAULT
                                );
                              }
                              return;
                            }
                          }}
                          className="mobile-buttons"
                          dropdownStyle={{ width: '310px' }}
                          alignment={{
                            horizontal: 'right',
                            vertical: 'bottom',
                          }}
                          isMobile={isMobile}
                        />
                      </div>

                      <ExportButton enabled={state.token != null} />
                    </>
                  ) : (
                    <>
                      <InputField
                        type="search"
                        id="search"
                        placeholder="Search for systems, properties or components"
                        value={searchQuery}
                        useAutocomplete={true}
                        autocompleteIsLoading={searchLoading}
                        submitLabel="Search"
                        searchResults={searchResults}
                        onSubmit={handleSearch}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={handleSearchClear}
                        onClickOptionSearch={(option) => {
                          redirectToSystemDetails(option);
                        }}
                        extraClasses="ai-button"
                      />

                      <Button
                        type={ButtonType.PRIMARY}
                        icon={IconType.AiChat}
                        label="AI-Search"
                        onClick={() => setAiModalOpen(true)}
                        state={
                          state.isLoading
                            ? ButtonState.DISABLED
                            : ButtonState.DEFAULT
                        }
                        extraClasses="ai-button"
                      />
                      <AIModal
                        isOpen={aiModalOpen}
                        onClose={() => setAiModalOpen(false)}
                        OnShowResults={(filters) => {
                          filters.forEach((filter) => {
                            actions.addAccessoryFilter(filter);
                          });
                        }}
                        labels={
                          state.configuration.smartSystemSelectorContent[0]
                            .aiSearchLabels
                        }
                      />

                      <SelectField
                        id="sorting"
                        options={[
                          {
                            label: 'Relevance',
                            name: 'Relevance',
                            value: 'Relevance',
                          },
                          ...(state.contentHubData
                            ?.attributesForSortingDropdown || []),
                        ]}
                        // value={state.contentHubData?.sortFilter?.sortName}
                        onChange={(value) => {
                          const [field, direction] = value.split('-');

                          try {
                            actions.setSorting(
                              field,
                              direction as TableHeadCellSorting
                            );
                          } catch (error) {
                            if (field === 'Relevance') {
                              actions.setSorting(
                                '',
                                TableHeadCellSorting.DEFAULT
                              );
                            }
                            return;
                          }
                        }}
                        style={{ width: '140px' }}
                        dropdownStyle={{ width: '310px' }}
                        alignment={{ horizontal: 'right', vertical: 'bottom' }}
                        isMobile={isMobile}
                      />

                      <ExportButton enabled={state.token != null} />
                    </>
                  )}

                  {!hookIsMobile('--from-desktop') && (
                    <div className="o-flex o-flex--jc-center o-flex--ai-center o-gap-2 u-hide-tablet">
                      <button
                        className={classNames({
                          'u-cursor-pointer u-cursor-pointer--20 u-fontsize-0':
                            true,
                          'u-color-subtle':
                            !isMobileView && viewType === ViewType.TABLE,
                          'u-color-inactive':
                            isMobileView || viewType !== ViewType.TABLE,
                        })}
                        onClick={() =>
                          !isMobileView && setViewType(ViewType.TABLE)
                        }
                        disabled={isMobileView}
                        aria-label="Table view"
                        title={
                          isMobileView
                            ? 'Table view only available on larger screens'
                            : 'Switch to table view'
                        }
                      >
                        <Icon
                          iconType={IconType.TableView}
                          className="u-icon-tiny"
                        />
                      </button>

                      <button
                        className={classNames({
                          'u-cursor-pointer u-fontsize-0': true,
                          'u-color-subtle':
                            isMobileView || viewType === ViewType.GRID,
                          'u-color-inactive':
                            !isMobileView && viewType !== ViewType.GRID,
                        })}
                        onClick={() => setViewType(ViewType.GRID)}
                        aria-label="Grid view"
                        title="Switch to grid view"
                      >
                        <Icon
                          iconType={IconType.GridView}
                          className="u-icon-tiny"
                        />
                      </button>
                    </div>
                  )}
                </div>

                <Facets
                  amount={state.contentHubData?.totalCount || 0}
                  facets={[
                    ...state.selectedFacets
                      .map((m) =>
                        createAccessoryFilterFromFacetProps(
                          m,
                          state.contentHubData.facets
                        )
                      )
                      .filter((f) => !f.hideThisValue),
                    ...(
                      state.contentHubData.selectFilters?.selectedTextQueries ||
                      []
                    ).map((textQuery) => ({
                      key: `text-query-${textQuery}`,
                      id: `text-query-${textQuery}`,
                      label: `Search: ${textQuery}`,
                      value: textQuery,
                      filterType: FilterType.SEARCH,
                      includedInTopFilter: false,
                      hideThisValue: false,
                    })),
                  ]}
                  onRemoveFacet={(filter: TagProps) => {
                    const accessoryFilter: IAccessoryFilter = {
                      key: filter.label,
                      id: String(filter.id),
                      label: filter.label,
                      value: filter.label,
                      filterType: filter.filterType || FilterType.CHECKBOX,
                      includedInTopFilter: filter.includedInTopFilter || false,
                    };
                    actions.removeAccessoryFilter(accessoryFilter);
                  }}
                  onClear={() => actions.clearAccessoryFilters()}
                />

                {/* Show notification when forced to grid view */}
                {isMobileView && viewType === ViewType.TABLE && (
                  <div
                    className="c-product-lister__mobile-notice u-mb-3"
                    aria-live="polite"
                  >
                    <Icon
                      iconType={IconType.Info}
                      className="u-icon-tiny u-mr-1"
                    />
                    <span className="u-text-small noselect">
                      Table view is only available on larger screens.
                    </span>
                  </div>
                )}
                {/* TABLE CONTENT */}
                {!state.isLoading && state.contentHubData?.list.length === 0 ? (
                  <Box
                    title="No results found for your criteria"
                    boxPicture={PictogramType.NoResults}
                  >
                    <p>
                      Check your spelling and try again. Use different or more
                      general keywords. Try fewer keywords for a broader search.
                    </p>
                    <p>
                      Still no luck?{' '}
                      <a href="mailto:#">Contact our support team</a> for
                      further assistance.
                    </p>
                  </Box>
                ) : (
                  <>
                    <div
                      className={classNames({
                        'c-product-lister__grid-table-list': true,
                        'c-product-lister__grid-table-list--table':
                          effectiveViewType === ViewType.TABLE,
                      })}
                    >
                      {effectiveViewType === ViewType.TABLE && (
                        <div className="c-product-lister__table">
                          <Table
                            colDefs={[
                              ...colDefs,
                              {
                                field: 'actions',
                                isRightSticky: true,
                                columnSize: TableHeadCellColumnSize.SMALL,
                                cellRenderer: (params) => (
                                  <aside className="a-btn-group">
                                    <Link
                                      variant={LinkVariant.BUTTON}
                                      icon={{
                                        iconType:
                                          actions.isFavorite(
                                            params.rowData.id
                                          ) && state.token
                                            ? IconType.FavoritesAdded
                                            : IconType.Favorites,
                                      }}
                                      description={
                                        actions.isFavorite(params.rowData.id)
                                          ? 'Remove from bookmarks'
                                          : 'Add to bookmarks'
                                      }
                                      iconTooltip={
                                        state.token
                                          ? undefined
                                          : {
                                              content:
                                                'You need to login to use this functionality',
                                              placement: TooltipPlacement.RIGHT,
                                              interactive: true,
                                              maxWidth: 350,
                                            }
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        //e.target.classes('a-link--pulse');
                                        console.log(
                                          'Bookmark toggle clicked for:',
                                          params.rowData
                                        );
                                        // set additional class on Link
                                        e.currentTarget.classList.add(
                                          'a-link--pulse'
                                        );

                                        handleBookmarkToggle(
                                          e.currentTarget,
                                          params.rowData.id
                                        );
                                      }}
                                    />
                                    <Link
                                      variant={LinkVariant.BUTTON}
                                      icon={{ iconType: IconType.Show }}
                                      description="Quick view"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickView(params.rowData.id);
                                      }}
                                    />
                                  </aside>
                                ),
                              },
                            ]}
                            onRowSelect={(row, isSelected) => {
                              if (isSelected) {
                                // Get first coldef to use for getting title value
                                actions.addToSelectedSystems(row);
                              } else {
                                actions.removeSelectedSystem(row.id);
                              }
                            }}
                            onRowClick={(row) => {
                              redirectToSystemDetails(row.link);
                            }}
                            onSelectAll={(isSelected) => {
                              handleSelectAllToggle(isSelected);
                            }}
                            allSelected={
                              state.contentHubData.list.length > 0 &&
                              state.contentHubData.list.every((row) =>
                                state.selectedSystems.some(
                                  (system) => system.id === row.id
                                )
                              )
                            }
                            selectedSystems={state.selectedSystems}
                            rowData={rowData}
                            isLoading={state.isLoading}
                            onTableSort={handleTableSort}
                          />
                        </div>
                      )}

                      {effectiveViewType === ViewType.GRID && (
                        <div
                          className={classNames(
                            'c-product-lister__grid-list u-pbs-3 o-max-col-grid o-max-col-grid--4',
                            {
                              'c-product-lister__grid-skeleton':
                                state.isLoading,
                            }
                          )}
                        >
                          {/* We render empty cards because the cards are being mapped with state data. */}
                          {/* That means that the grid will only display when state has been loaded. */}
                          {/* Therefore the loading state on the data cards is always false */}
                          {state.isLoading
                            ? Array.from({ length: 8 }).map((_, index) => (
                                <Card
                                  key={`skeleton-${index}`}
                                  id={`skeleton-${index}`}
                                  title="Loading..."
                                  isLoading={true}
                                />
                              ))
                            : state.contentHubData.list.map((card, index) => (
                                <Card
                                  key={index}
                                  id={card.id}
                                  hasCheckbox
                                  title={card.title}
                                  systemCode={card?.systemCode}
                                  image={{ src: card.image, alt: card.title }}
                                  link={card.link}
                                  performanceAttributes={state.contentHubData.gridPerformanceFields.map(
                                    (pf) => ({
                                      label: pf.label ?? pf.name,
                                      value:
                                        card.properties.find(
                                          (prop) => prop.key === pf.name
                                        )?.value || '',
                                      color: pf.color,
                                    })
                                  )}
                                  specs={card.specs}
                                  footerBtnLabel={card.viewCTA}
                                  isBookmarked={actions.isFavorite(card.id)}
                                  isLoggedIn={state.token != null}
                                  onBookmarkToggle={(currentTarget) =>
                                    handleBookmarkToggle(currentTarget, card.id)
                                  }
                                  onSelect={(system, isSelected) => {
                                    if (isSelected) {
                                      actions.addToSelectedSystems(system);
                                    } else {
                                      actions.removeSelectedSystem(system.id);
                                    }
                                  }}
                                  selectedSystems={state.selectedSystems}
                                  onButtonBottom={() =>
                                    handleQuickView(card.id)
                                  }
                                  card={
                                    rowData.some((r) => r.id === card.id)
                                      ? rowData.find((r) => r.id === card.id)
                                      : null
                                  }
                                  onCardClick={() =>
                                    redirectToSystemDetails(card.link)
                                  }
                                />
                              ))}
                        </div>
                      )}
                    </div>

                    <footer className="m-comp-footer">
                      <Pagination
                        currentPage={state.contentHubData.currentPage}
                        total={Math.ceil(state.contentHubData.pageCount / 1)}
                        handleGoToFirst={() => {
                          actions.getSearchData({
                            ...state.query,
                            pagination: {
                              ...state.query.pagination,
                              pageNumber: 1,
                              pageSize: state.selectedPageSize,
                            },
                          });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        handleGoToPrevious={() => {
                          actions.getSearchData({
                            ...state.query,
                            pagination: {
                              ...state.query.pagination,
                              pageNumber: state.contentHubData.currentPage - 1,
                              pageSize: state.selectedPageSize,
                            },
                          });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        handleGoToNext={() => {
                          actions.getSearchData({
                            ...state.query,
                            pagination: {
                              ...state.query.pagination,
                              pageNumber: state.contentHubData.currentPage + 1,
                              pageSize: state.selectedPageSize,
                            },
                          });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        handleGoToLast={() => {
                          const search = {
                            ...state.query,
                            pagination: {
                              ...state.query.pagination,
                              pageNumber: state.contentHubData.pageCount,
                              pageSize: state.selectedPageSize,
                            },
                          };
                          actions.getSearchData(search);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        systemSelectorLabels={
                          state.configuration.smartSystemSelectorContent[0]
                            .systemSelectorLabels
                        }
                        loading={state.isLoading}
                      />
                    </footer>
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
      <div>
        {/* Selection Overlay */}
        <div className="selection-overlay">
          <SelectActionWidget
            selectedSystems={state.selectedSystems}
            firstColDef={colDefs[0]?.field}
            onRemoveSystem={(id: number) => {
              actions.removeSelectedSystem(id);
            }}
            onClearAll={() => {
              actions.clearSelectedSystems();
            }}
            onCompare={() => {
              const ids = state.selectedSystems.map((system) => system.id);
              redirectToComparePage(ids);
            }}
            onSaveFavorites={() => {
              // actions.saveSelectedSystemsAsFavorites();
            }}
          />
        </div>
        {/* Quick View Slideout */}
        {quickViewCard && (
          <QuickViewPanel
            title={quickViewCard.title}
            image={quickViewCard.image}
            isVisible={quickViewVisible}
            onClose={() => setQuickViewVisible(false)}
            systemCode={quickViewCard.systemCode}
            attributes={quickViewCard.properties}
            featuredLabels={
              state.contentHubData?.gridFeaturedFields?.map(
                (field) => field.name as string
              ) || []
            }
            isLoggedIn={state.token != null}
            saved={quickViewCard.favoriteCTA}
            smartListerLabels={
              state.configuration.smartSystemSelectorContent[0]
                .smartListerLabels
            }
            onViewDetails={() => {
              redirectToSystemDetails(quickViewCard.link);
            }}
          />
        )}
        {/* Info Panel Slideout */}
        {state.infoPanel?.isVisible && (
          <InfoPanel
            isVisible={state.infoPanel.isVisible}
            onClose={() => actions.clearInfoPanel()}
            title={state.infoPanel.title}
            richText={state.infoPanel.richtText}
          />
        )}
      </div>
    </div>
  );
};

export const Gary: React.FC = () => {
  // const [,actions]: [IState, IActions] = useReactHookz();

  return (
    <>
      <h3>Component Rendered!</h3>
    </>
  );
};

export const SystemSelectorWrapper: React.FC = () => {
  // const [isMobileView, setIsMobileView] = useState(false);
  return <Gary />;
};
